import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "reactstrap";
import { Pencil, Plus, Search, ShieldCheck, Trash2, X } from "lucide-react";
import { deleteAdmin, getAdminList } from "../../../redux/admin/action";
import { usePermissions } from "../../../Hooks/usePermissions";
import ModalDelete from "../../Common/Popup/ModalDelete";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import AdminTabs from "./AdminTabs";
import "./AdminList.css";

const STATUS = {
  ACTIVE: 1,
  INACTIVE: 2,
  BLOCKED: 4,
};

const statusPill = (status) => {
  switch (status) {
    case STATUS.ACTIVE:
      return { label: "Active", className: "admin-pill admin-pill--success" };
    case STATUS.INACTIVE:
      return { label: "Inactive", className: "admin-pill admin-pill--muted" };
    case STATUS.BLOCKED:
      return { label: "Blocked", className: "admin-pill admin-pill--danger" };
    default:
      return { label: "—", className: "admin-pill" };
  }
};

const DEBOUNCE_MS = 350;

const AdminList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUserId } = usePermissions();

  const [params, setParams] = useState({
    page: 1,
    search_key: "",
    status: "",
    per_page: "10",
  });
  const [searchDraft, setSearchDraft] = useState("");
  const [modal, setModal] = useState(false);
  const [targetAdmin, setTargetAdmin] = useState(null);
  const [, setLoading] = useState(false);

  const adminlist = useSelector(({ admin: { adminList } }) => adminList);
  const admins = useMemo(() => adminlist?.admins || [], [adminlist]);
  const paginationMeta = adminlist?.pagination_meta;

  useEffect(() => {
    dispatch(getAdminList(params));
  }, [dispatch, params]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setParams((p) => ({ ...p, page: 1, search_key: searchDraft }));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  const handlePageChange = (page) => setParams((p) => ({ ...p, page }));

  const handleStatusChange = (e) =>
    setParams((p) => ({ ...p, page: 1, status: e.target.value }));

  const handlePerPageChange = (e) =>
    setParams((p) => ({ ...p, page: 1, per_page: e.target.value }));

  const openDelete = (admin) => {
    setTargetAdmin(admin);
    setModal(true);
  };

  const confirmDelete = () => {
    if (!targetAdmin?.id) return;
    dispatch(
      deleteAdmin(targetAdmin.id, setLoading, () => {
        dispatch(getAdminList(params));
      }),
    );
  };

  return (
    <div className="admin-list">
      <header className="admin-list__header">
        <div>
          <h2 className="admin-list__title">Admins</h2>
          <p className="admin-list__subtitle">
            Manage dashboard access and per-module permissions.
          </p>
        </div>
        <button
          type="button"
          className="admin-list__add"
          onClick={() => navigate("/admin/add")}
        >
          <Plus size={16} /> Add admin
        </button>
      </header>

      <AdminTabs />

      <div className="admin-list__toolbar">
        <div className="admin-list__search">
          <Search size={16} className="admin-list__search-icon" />
          <input
            type="text"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search by name, email, phone…"
            aria-label="Search admins"
          />
          {searchDraft && (
            <button
              type="button"
              className="admin-list__search-clear"
              aria-label="Clear search"
              onClick={() => setSearchDraft("")}
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          className="admin-list__status-filter"
          value={params.status}
          onChange={handleStatusChange}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value={String(STATUS.ACTIVE)}>Active</option>
          <option value={String(STATUS.INACTIVE)}>Inactive</option>
          <option value={String(STATUS.BLOCKED)}>Blocked</option>
        </select>
      </div>

      <div className="admin-list__table-wrap">
        <table className="admin-list__table">
          <thead>
            <tr>
              <th scope="col" className="admin-list__cell-index">
                #
              </th>
              <th scope="col">Admin</th>
              <th scope="col">Contact</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col" className="admin-list__cell-actions">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan={6} className="admin-list__empty">
                  No admins found.
                </td>
              </tr>
            ) : (
              admins.map((admin, idx) => {
                const isSelf = admin.id === currentUserId;
                const status = statusPill(admin.status);
                const displayIndex =
                  idx + 1 + ((params.page || 1) - 1) * Number(params.per_page || 10);
                return (
                  <tr key={admin.id} className={isSelf ? "admin-list__row--self" : ""}>
                    <td className="admin-list__cell-index">{displayIndex}</td>
                    <td>
                      <div className="admin-list__name">
                        <span className="admin-list__fullname">
                          {admin.first_name} {admin.last_name}
                        </span>
                        {isSelf && (
                          <span className="admin-list__self-badge">You</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="admin-list__contact">
                        <span>{admin.email_id}</span>
                        <span className="admin-list__muted">
                          {admin.country_code} {admin.mobile_number}
                        </span>
                      </div>
                    </td>
                    <td>
                      {admin.is_super_admin ? (
                        <span className="admin-pill admin-pill--primary">
                          <ShieldCheck size={12} /> Super Admin
                        </span>
                      ) : (
                        <span className="admin-pill admin-pill--muted">Admin</span>
                      )}
                    </td>
                    <td>
                      <span className={status.className}>{status.label}</span>
                    </td>
                    <td className="admin-list__cell-actions">
                      <div className="admin-list__row-actions">
                        <button
                          type="button"
                          className="admin-list__icon-btn"
                          onClick={() => navigate(`/admin/update/${admin.id}`)}
                          title="Edit"
                          aria-label={`Edit ${admin.first_name}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="admin-list__icon-btn admin-list__icon-btn--danger"
                          onClick={() => !isSelf && openDelete(admin)}
                          disabled={isSelf}
                          title={
                            isSelf
                              ? "You can't delete your own account"
                              : "Delete"
                          }
                          aria-label={`Delete ${admin.first_name}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <footer className="admin-list__footer">
        <div className="admin-list__per-page">
          Rows per page
          <select
            className="form-select"
            value={params.per_page}
            onChange={handlePerPageChange}
            aria-label="Rows per page"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        {paginationMeta && (
          <CommonPagination
            currentPage={paginationMeta.current_page}
            totalPages={paginationMeta.pages}
            onPageChange={handlePageChange}
          />
        )}
      </footer>

      <Modal isOpen={modal} toggle={() => setModal((s) => !s)}>
        <ModalDelete
          setModal={setModal}
          currentComponent={
            targetAdmin
              ? {
                  ...targetAdmin,
                  title: `${targetAdmin.first_name} ${targetAdmin.last_name}`,
                }
              : {}
          }
          deleteComponent={confirmDelete}
          itemType="admin"
          modalTitle="Admin"
        />
      </Modal>
    </div>
  );
};

export default AdminList;
